import React, { MouseEvent, ReactNode } from 'react';
import Breadcrumb from '../../Breadcrumb';
import CustomTabv2, { CustomTabItem } from '../../custom/CustomTabv2';
import EnrichmentListEditorComponent from './EnrichmentListEditorComponent';
import EnrichmentListAccessComponent from './EnrichmentListAccessComponent';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { RootState } from '../../../redux/store';
import { changeListVariable, changeListVariableDirty, changeListVariableIsUpdate, clearListVariable } from 'redux/actions/ListVariableActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import ComponentName from '../../../constant/ComponentName';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import CustomEditorItemComponent from '../../custom/CustomEditorItemComponent';
import MessageUtil from '../../../util/MessageUtil';
import LanguageUtil from '../../../util/LanguageUtil';
import ListVariableService from '../../../service/ListVariableService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../../deploy/UndeployImpactDialog';
import { ListVariable } from '../../../domain/enrichment/EnrichmentDomain';
import CustomLoading from 'components/custom/CustomLoading';
import RoleHelper from '../../../RoleHelper';
import ComponentAuditLog from '../../ComponentAuditLog';
import { clearPage } from 'redux/actions/PageActions';
import { isStringValid } from '../../../util/Generator';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../../helpers/StandardEditorContainerComponent';
import ComponentErrorLog from '../../ComponentErrorLog';
import { EnrichedComponentCategory } from '../../../domain/category/CategoryOperationDomains';
import { ConfirmPopup } from 'primereact/confirmpopup';
import eq = query.eq;

class EnrichmentListEditorContainer extends StandardEditorContainerComponent<Props, State, ListVariable> {
  public state = new State();

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly listVariableService: ListVariableService = ServiceContainer.getInstance().get(ServiceNames.LIST_VARIABLE);

  private computation: subscribe.Computation | undefined;
  private listVariableSubscription: subscribe.Subscription | undefined;
  private initialDirtyResetTimer: ReturnType<typeof setTimeout> | undefined;
  private syncDirtyResetTimer: ReturnType<typeof setTimeout> | undefined;
  private isInitialDirtyResetScheduled: boolean = false;

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.ENRICHMENT,
      icon: 'enrichment',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      listVariable: { listVariable, isDirty },
      isUpdate,
    } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={listVariable.name}
          description={listVariable.description}
          status={listVariable?.stage || 'DRAFT'}
          component={ComponentName.LIST_VARIABLE}
          isUpdate={isUpdate}
          icon={'enrichment'}
          onChangeName={this.onChangeName}
          onChangeDescription={this.onChangeDescription}
          onSave={this.saveVariable}
          onDeploy={this.deployVariable}
          onUndeploy={this.undeployVariable}
          onRemove={this.removeVariable}
          disableSave={!RoleHelper.canEdit('EnrichmentList')}
          disableRemove={!RoleHelper.canEdit('EnrichmentList')}
          disableNameChange={!RoleHelper.canEdit('EnrichmentList')}
          disableDescriptionChange={!RoleHelper.canEdit('EnrichmentList')}
          disableDeploy={!RoleHelper.canDeploy('EnrichmentList') || isDirty}
          disableUndeploy={!RoleHelper.canDeploy('EnrichmentList')}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full py-3 '}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearListVariable } = this.props;
    if (this.initialDirtyResetTimer) {
      clearTimeout(this.initialDirtyResetTimer);
    }
    if (this.syncDirtyResetTimer) {
      clearTimeout(this.syncDirtyResetTimer);
    }
    clearListVariable();
    super.componentWillUnmount();
  }

  public componentDidUpdate(prevProps: Props) {
    const {
      listVariable: { isUpdate },
      changeListVariableDirty,
    } = this.props;
    if (!prevProps.listVariable.isUpdate && isUpdate && !this.isInitialDirtyResetScheduled) {
      this.isInitialDirtyResetScheduled = true;
      this.initialDirtyResetTimer = setTimeout(() => {
        changeListVariableDirty(false);
      }, 1200);
    }
  }

  protected getItemIdFromRoute = () => {
    const { changeListVariableIsUpdate } = this.props;
    const id = this.props.router.params.id;
    changeListVariableIsUpdate(id != undefined);
    return id;
  };

  protected _startTracker = async () => {
    const variableId = this.getItemIdFromRoute();
    if (variableId == undefined) {
      this.setState({ loading: false });
    }
    const listVariableQuery = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).where(eq('name', variableId));
    this.listVariableSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENTS, listVariableQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.listVariableSubscription]);
  };

  protected tracker = async () => {
    const { changeListVariableIsUpdate, changeListVariable, changeListVariableDirty } = this.props;
    if (this.listVariableSubscription?.ready()) {
      const variableId = this.getItemIdFromRoute();
      if (variableId == undefined) {
        changeListVariableIsUpdate(false);
        changeListVariableDirty(false);
        RoleHelper.setCurrentAccess('Edit');
        this.setState({ loading: false });
        return;
      }
      const listVariableQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).where(eq('id', variableId)).build();
      const listVariable: ListVariable | undefined = await this.repository.find(listVariableQuery);
      if (listVariable == undefined) {
        return;
      }
      changeListVariable(listVariable);
      changeListVariableDirty(false);
      if (this.syncDirtyResetTimer) {
        clearTimeout(this.syncDirtyResetTimer);
      }
      this.syncDirtyResetTimer = setTimeout(() => {
        changeListVariableDirty(false);
      }, 1200);
      RoleHelper.setCurrentAccess(listVariable);
      this.setState({ loading: false, updated: this.state.updated + 1 });
    }
  };

  protected _stopTracker = async () => {
    await this.listVariableSubscription?.stop();
    this.computation?.stop();
  };

  private resubscribeTracker = async () => {
    this.computation?.onStop(this.startTracker);
    await this.stopTracker();
  };

  private onChangeName = (name: string) => {
    const {
      listVariable: { listVariable },
      changeListVariable,
    } = this.props;
    if (isStringValid(name)) {
      listVariable.name = name.trim();
      changeListVariable(listVariable);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      listVariable: { listVariable },
      changeListVariable,
    } = this.props;
    listVariable.description = description;
    changeListVariable(listVariable);
  };

  private switchToUpdate = async () => {
    const {
      listVariable: { listVariable },
      enterComponent,
    } = this.props;
    const {
      router: { navigate, params },
    } = this.props;
    if (listVariable.name == '') return;
    enterComponent({ component: ComponentName.ENRICHMENT, id: listVariable.name });
    navigate(`/enrichment${params.folderId ? `/folder/${params.folderId}` : ''}/list/${listVariable.name}`, { replace: true });
    setTimeout(() => {
      this.resubscribeTracker();
    });
  };

  private saveVariable = async () => {
    const {
      listVariable: { listVariable },
      isUpdate,
      changeListVariableIsUpdate,
      lastCategory,
    } = this.props;
    try {
      const isSaved = await this.listVariableService.save(listVariable, isUpdate, lastCategory);
      if (isSaved) {
        changeListVariableIsUpdate(true);
        if (!isUpdate) await this.switchToUpdate();
      }
    } catch (e) {
      console.error(e);
    }
  };

  private removeVariable = (e: MouseEvent<HTMLElement>) => {
    const {
      listVariable: { listVariable },
    } = this.props;
    const {
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        try {
          const success = await this.listVariableService.delete(listVariable.name);
          if (success) {
            navigate(`/enrichment`);
          }
        } catch (e: any) {
          console.error(e);
        }
      },
      message: LanguageUtil.get({ id: 'messages.delete1', defaultMessage: 'Are you sure you want to remove?' }),
    });
  };

  private deployVariable = (e: MouseEvent<HTMLElement>) => {
    const {
      listVariable: { listVariable },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.listVariableService.save(listVariable, true, {} as EnrichedComponentCategory);
        await this.listVariableService.deploy(listVariable);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
    });
  };
  private undeployVariable = async (e: MouseEvent<HTMLElement>) => {
    const {
      listVariable: { listVariable },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => confirmUndeployWithImpact(ComponentName.LIST_VARIABLE, [listVariable.name], () => this.listVariableService.undeploy(listVariable.name)),
      message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
    });
  };

  protected getTabs(): CustomTabItem[] {
    const { updated } = this.state;
    return [
      {
        key: 'Editor',
        label: LanguageUtil.get({ id: 'common.tabs.editor', defaultMessage: 'Editor' }),
        content: <EnrichmentListEditorComponent updated={updated} />,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <EnrichmentListAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.LIST_VARIABLE, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.LIST_VARIABLE} documentName={this.props.listVariable.listVariable.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.LIST_VARIABLE, true) && this.environmentUtil.isAuditLogVisible(),
      },
      {
        key: 'Error',
        label: LanguageUtil.get({ id: 'common.tabs.error', defaultMessage: 'Error' }),
        content: <ComponentErrorLog component={ComponentName.LIST_VARIABLE} documentName={this.props.listVariable.listVariable.name} />,
        visible: RoleHelper.canManage(ComponentName.LIST_VARIABLE, true),
      },
    ];
  }

  protected changeItem = (item: ListVariable) => {
    const { changeListVariable } = this.props;
    changeListVariable(item);
  };
}

interface Props extends FormicaProps, ReduxProps {}

class State {
  loading: boolean = true;
  updated: number = 0;
}

const mapStateToProps = (state: RootState) => ({
  listVariable: state.listVariable,
  isUpdate: state.listVariable.isUpdate,
  lastCategory: state.page.lastCategory,
});

const mapDispatchToProps = {
  changeListVariableIsUpdate,
  changeListVariable,
  changeListVariableDirty,
  clearListVariable,
  enterComponent,
  leaveComponent,
  clearPage,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(EnrichmentListEditorContainer));
